'use client';
/* eslint-disable tailwindcss/no-arbitrary-value */
import {
  Popover,
  PopoverContent,
  PopoverHandler,
} from '@material-tailwind/react';
import cn from 'classnames';
import { addMonths } from 'date-fns';
import { useBreakpoint } from 'hooks/useBreakpoint';
import React, { useMemo, useState, useCallback } from 'react';
import { createMonthCaptionComponent } from '$/components/DateRangePicker/components/MonthCaption';
import { SingleDateCalendar } from '$/components/DateRangePicker/components/SingleDateCalendar';
import {
  defaultContentClassName,
  defaultTriggerButtonClassName,
  defaultTriggerWrapperClassName,
  defaultMonthNames,
} from '$/components/DateRangePicker/constants';
import { useDateRangeClassNames } from '$/components/DateRangePicker/hooks/useDateRangeClassNames';
import {
  DateRangePickerStaticText,
  type DateRangePickerStaticTextType,
} from '$/components/DateRangePicker/i18n';
import { parseTranslationList } from '$/components/DateRangePicker/utils';
import Calendar from '$/components/icons/Calendar';
import { useClientContext } from '$/modules/context/client';
import { getClientTranslations } from '$/modules/i18n/client.index';

type DateInputProps = {
  id?: string;
  value: Date;
  minDate?: Date;
  maxDate?: Date;
  onChange: (date: Date) => void;
  disabled?: boolean;
  className?: string;
};

// Format date for display respecting language (en vs zh)
export function formatDisplayDate(date: Date, lang: string): string {
  if (!date) {
    return '';
  }

  if (lang.startsWith('zh')) {
    // Chinese format: YYYY年M月D日
    const y = date.getFullYear();
    const m = date.getMonth() + 1;
    const d = date.getDate();
    return `${y}年${m}月${d}日`;
  }
  // English format: Month D, YYYY
  return date.toLocaleDateString(lang || 'en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

export const DateInput: React.FC<DateInputProps> = ({
  id,
  value,
  minDate,
  maxDate,
  onChange,
  disabled = false,
  className = '',
}) => {
  const context = useClientContext();
  const lang = context.lang;

  const staticText = getClientTranslations<DateRangePickerStaticTextType>(
    DateRangePickerStaticText,
    context,
  );

  const [isOpen, setIsOpen] = useState(false);
  const selected = value;

  const breakpoint = useBreakpoint({ _: '_', sm: 'sm', md: 'md', lg: 'lg' });
  const isMobileBreakpoint = breakpoint === '_' || breakpoint === 'sm';

  // Month/weekday names translation
  const monthNames = useMemo(
    () => parseTranslationList(staticText.monthNames, defaultMonthNames),
    [staticText.monthNames],
  );

  const mergedClassNames = useDateRangeClassNames();
  const monthCaptionComponent = useMemo(
    () => createMonthCaptionComponent({ monthNames, localeCode: lang }),
    [monthNames, lang],
  );

  const handleOpen = () => {
    if (disabled) return;
    setIsOpen(true);
  };

  const handleClose = useCallback(() => setIsOpen(false), []);

  const handleSelect = useCallback(
    (day?: Date) => {
      if (!day) return;
      onChange(day);
      handleClose();
    },
    [onChange, handleClose],
  );

  const displayLabel = useMemo(
    () => (selected ? formatDisplayDate(selected, lang) : ''),
    [selected, lang],
  );

  const triggerWrapperClassName = `${defaultTriggerWrapperClassName} ${className}`;

  const [currentMonth, setCurrentMonth] = useState<Date>(value ?? new Date());
  const handleMonthChange = useCallback((month: Date) => {
    setCurrentMonth(month);
  }, []);

  const dayPickerProps = useMemo(
    () => ({
      mode: 'single' as const,
      selected,
      onSelect: handleSelect,
      hideNavigation: true,
      captionLayout: 'dropdown' as const,
      components: { MonthCaption: monthCaptionComponent },
      classNames: mergedClassNames,
      disabled: (() => {
        if (minDate && maxDate) {
          return [{ before: minDate }, { after: maxDate }];
        }
        if (minDate) {
          return { before: minDate };
        }
        if (maxDate) {
          return { after: maxDate };
        }
        return undefined;
      })(),
      startMonth: minDate,
      endMonth: maxDate,
      month: isMobileBreakpoint ? currentMonth : undefined,
      onMonthChange: isMobileBreakpoint ? handleMonthChange : undefined,
    }),
    [
      selected,
      handleSelect,
      monthCaptionComponent,
      mergedClassNames,
      minDate,
      maxDate,
      isMobileBreakpoint,
      currentMonth,
      handleMonthChange,
    ],
  );
  const monthKey = (d: Date) => d.getFullYear() * 12 + d.getMonth();
  const minMonthKey = minDate ? monthKey(minDate) : undefined;
  const maxMonthKey = maxDate ? monthKey(maxDate) : undefined;
  const currentMonthKey = monthKey(currentMonth);
  const canGoToPreviousMonth =
    isMobileBreakpoint &&
    (minMonthKey === undefined || currentMonthKey > minMonthKey);
  const canGoToNextMonth =
    isMobileBreakpoint &&
    (maxMonthKey === undefined || currentMonthKey < maxMonthKey);
  const handlePreviousMonthClick = () =>
    setCurrentMonth((prev) => {
      const next = addMonths(prev, -1);
      if (minDate && monthKey(next) < monthKey(minDate)) return prev;
      return next;
    });
  const handleNextMonthClick = () =>
    setCurrentMonth((prev) => {
      const next = addMonths(prev, 1);
      if (maxDate && monthKey(next) > monthKey(maxDate)) return prev;
      return next;
    });

  const calendarContent = (
    <SingleDateCalendar
      dayPickerProps={dayPickerProps}
      doneLabel={staticText.done}
      isMobileSingleMonth={isMobileBreakpoint}
      canGoToNextMonth={canGoToNextMonth}
      canGoToPreviousMonth={canGoToPreviousMonth}
      onNextMonthClick={handleNextMonthClick}
      onPreviousMonthClick={handlePreviousMonthClick}
      onDoneClick={handleClose}
    />
  );

  const popoverBody = isMobileBreakpoint ? (
    <div className="pointer-events-auto fixed inset-0 z-[--z-index-dropdown] flex items-center justify-center px-4">
      <div
        aria-hidden
        className="absolute inset-0 bg-black/30"
        onClick={handleClose}
      />
      <div className="relative z-10 max-h-[calc(100vh-32px)] w-full max-w-general/content-max-width rounded-radius/button border border-borders/default bg-colors/neutral-white">
        {calendarContent}
      </div>
    </div>
  ) : (
    calendarContent
  );

  const popoverContentClassName = cn(defaultContentClassName, {
    'pointer-events-none border-0 bg-transparent shadow-none w-full max-w-full':
      isMobileBreakpoint,
  });

  return (
    <Popover open={isOpen} handler={setIsOpen} placement="bottom-start">
      <PopoverHandler>
        <div className={triggerWrapperClassName}>
          <button
            type="button"
            id={id}
            onClick={handleOpen}
            disabled={disabled}
            className={defaultTriggerButtonClassName}
          >
            <span className="mr-2 text-gray-600">
              <Calendar />
            </span>
            {displayLabel ? (
              <span className="mhg-alpha-typography-form-input whitespace-nowrap text-text/primary">
                {displayLabel}
              </span>
            ) : null}
          </button>
        </div>
      </PopoverHandler>
      <PopoverContent className={popoverContentClassName}>
        {popoverBody}
      </PopoverContent>
    </Popover>
  );
};

DateInput.displayName = 'DateInput';
